import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  confirmPhoneCode,
  getPhoneVerificationConfig,
  invalidatePhoneVerificationConfig,
  sendPhoneCode,
  type PhoneCodeSession,
  type PhoneSendCredentials,
} from "../../services/phoneVerificationService";

/** Delays between automatic retries when the config request fails. */
export const CONFIG_RETRY_DELAYS_MS = [2000, 5000, 10000];

/** Message for a form submitted before the switch's state is known. */
export const PHONE_CONFIG_PENDING_MESSAGE = "Still checking phone verification — please try again in a moment.";

/**
 * Whether phone verification is switched on (backend-controlled). ``enabled`` is
 * ``null`` while unknown — still loading, or the request failed — and forms must not
 * submit a phone number until it resolves. ``refresh`` asks the backend again.
 */
export function usePhoneVerificationEnabled(): { enabled: boolean | null; refresh: () => void } {
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [generation, setGeneration] = useState(0);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const load = (attempt: number) => {
      getPhoneVerificationConfig()
        .then((c) => { if (!cancelled) setEnabled(!!c.enabled); })
        .catch(() => {
          if (cancelled || attempt >= CONFIG_RETRY_DELAYS_MS.length) return;
          timer = setTimeout(() => load(attempt + 1), CONFIG_RETRY_DELAYS_MS[attempt]);
        });
    };
    load(0);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [generation]);

  const refresh = useCallback(() => {
    invalidatePhoneVerificationConfig();
    setGeneration((g) => g + 1);
  }, []);

  return { enabled, refresh };
}

interface PhoneVerificationProps {
  /** The number to verify in E.164, or null while what's typed isn't a valid number yet. */
  phoneE164: string | null;
  /** True once this exact number is verified — shows the confirmation instead of the controls. */
  verified: boolean;
  onVerified: (token: string, phoneE164: string) => void;
  disabled?: boolean;
  /** The phone input itself. When given, "Send code" sits on the same row as it (like the
   * email field's), instead of on a row of its own beneath the input. */
  inputRow?: React.ReactNode;
  /** False renders just ``inputRow``, in the same place in the tree, so the input isn't
   * remounted (losing focus) when the switch turns out to be on, or once verified. */
  enabled?: boolean;
  /** Runs before a code is texted; throw to stop the send and show the error's message
   * (e.g. the profile page checks the number isn't another account's first). */
  beforeSend?: (phoneE164: string) => Promise<void>;
  /** Identifies the caller to the send-code endpoint: the user's token once signed in,
   * or the OTP-verified email during signup. A function is resolved at send time. */
  credentials?: PhoneSendCredentials | (() => Promise<PhoneSendCredentials>);
}

const RESEND_COOLDOWN_SECONDS = 30;

/**
 * "Send code" → 6-digit code → "Verify" for a phone number, mirroring the email OTP
 * controls on the signup form. Render it only while verification is enabled.
 */
const PhoneVerification: React.FC<PhoneVerificationProps> = ({
  phoneE164, verified, onVerified, disabled = false, inputRow, enabled = true, beforeSend, credentials,
}) => {
  const [confirmation, setConfirmation] = useState<PhoneCodeSession | null>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const recaptchaRef = useRef<HTMLDivElement>(null);

  // A code belongs to the number it was sent to; editing the number starts over.
  useEffect(() => {
    if (sentTo && sentTo !== phoneE164) {
      setConfirmation(null);
      setSentTo(null);
      setCode("");
      setError("");
      setCooldown(0);
    }
  }, [phoneE164, sentTo]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const handleSend = async () => {
    if (!phoneE164 || !recaptchaRef.current) return;
    setSending(true); setError("");
    try {
      if (beforeSend) await beforeSend(phoneE164);
      const sendCredentials = typeof credentials === "function" ? await credentials() : credentials;
      const result = await sendPhoneCode(phoneE164, recaptchaRef.current, sendCredentials);
      setConfirmation(result);
      setSentTo(phoneE164);
      setCode("");
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "We couldn’t send the code. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const handleVerify = async () => {
    if (!confirmation || !sentTo || code.length !== 6) return;
    setVerifying(true); setError("");
    try {
      const token = await confirmPhoneCode(confirmation, code);
      onVerified(token, sentTo);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Verification failed. Please try again.");
    } finally {
      setVerifying(false);
    }
  };

  const sendDisabled = disabled || sending || !phoneE164 || cooldown > 0;
  const verifyDisabled = disabled || verifying || code.length !== 6;

  const sendButton = (
    <button
      type="button"
      onClick={handleSend}
      disabled={sendDisabled}
      data-testid="phone-send-code"
      style={{
        whiteSpace: "nowrap", padding: inputRow ? "0 0.875rem" : "0.4rem 0.875rem", borderRadius: "6px",
        border: "1px solid #4F99BD", background: "#fff", color: "#005581",
        fontSize: "0.85rem", fontWeight: 600,
        cursor: sendDisabled ? "default" : "pointer",
        opacity: sendDisabled ? 0.6 : 1,
      }}
    >
      {sending ? "Sending…" : confirmation ? (cooldown > 0 ? `Resend (${cooldown}s)` : "Resend code") : "Send code"}
    </button>
  );

  // Send / code / errors only while there's something to verify. The tree shape stays the
  // same either way, so ``inputRow`` keeps its place.
  const active = enabled && !verified;

  return (
    <div>
      {inputRow ? (
        <div style={{ display: "flex", gap: "0.5rem" }}>
          {inputRow}
          {active && sendButton}
        </div>
      ) : (
        active && (
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginTop: "0.5rem" }}>
            {sendButton}
            {!confirmation && (
              <span style={{ fontSize: "0.75rem", color: "#6b7280" }}>We’ll text a code to verify this number.</span>
            )}
          </div>
        )
      )}

      {/* Invisible reCAPTCHA attaches here; must stay mounted while a code is being sent. */}
      <div ref={recaptchaRef} />

      {active && confirmation && (
        <div style={{ marginTop: "0.625rem", padding: "0.75rem", background: "#f0f7fb", border: "1px solid #d6e8f2", borderRadius: "8px" }}>
          <label htmlFor="phone-otp-code" style={{ fontSize: "0.8rem", marginBottom: "0.4rem", display: "block", color: "#374151" }}>
            Enter the 6-digit code we texted to {sentTo}
          </label>
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <input
              id="phone-otp-code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="123456"
              disabled={verifying || disabled}
              className="login-input"
              style={{ flex: 1, letterSpacing: "0.3em", fontWeight: 600 }}
            />
            <button
              type="button"
              onClick={handleVerify}
              disabled={verifyDisabled}
              style={{
                whiteSpace: "nowrap", padding: "0 1rem", borderRadius: "6px", border: "none",
                background: "linear-gradient(135deg, #4F99BD, #005581)", color: "#fff",
                fontSize: "0.85rem", fontWeight: 600,
                cursor: verifyDisabled ? "default" : "pointer",
                opacity: verifyDisabled ? 0.6 : 1,
              }}
            >
              {verifying ? "Verifying…" : "Verify"}
            </button>
          </div>
        </div>
      )}

      {enabled && verified && (
        <p style={{ fontSize: "0.8rem", color: "#16a34a", margin: "0.4rem 0 0", fontWeight: 600 }}>
          ✓ Phone verified
        </p>
      )}

      {active && error && <p role="alert" style={{ fontSize: "0.8rem", color: "#dc2626", margin: "0.4rem 0 0" }}>{error}</p>}
    </div>
  );
};

export default PhoneVerification;
